<template>
  <HeroSection 
    title="À propos & Contact"
    subtitle="Découvrez notre histoire, trouvez des réponses, ou contactez-nous directement."
    :isBig="false"
    bgImg="contact.jpg"
  />

  <!-- À propos -->
  <section class="py-16 px-4 md:px-32 bg-white">
    <div class="max-w-4xl mx-auto space-y-12">
      <!-- Notre histoire -->
      <div class="text-justify">
        <h2 class="text-2xl font-bold mb-4 text-center">Notre histoire</h2>
        <p class="mb-2 text-justify">
          Chez Nailit, nous croyons que chaque PME mérite un outil de gestion simple, puissant et humain.
        </p>
        <p class="mb-2 text-justify">
          Tout a commencé avec une passion partagée pour l'organisation et le développement. En tant qu'indépendant, notre fondateur a rapidement constaté un manque : les solutions existantes étaient soit trop complexes, soit trop rigides pour les petites structures.
        </p>
        <p class="mb-2 text-justify">
          En 2024, Nailit est né avec une ambition : offrir la flexibilité d'un outil sur-mesure, sans les inconvénients des logiciels "usine à gaz".
        </p>
      </div>

      <!-- Équipe -->
      <div class="bg-gray-50 rounded-lg p-6 shadow">
        <h3 class="font-bold text-center mb-2 text-lg">Une équipe à votre écoute</h3>
        <p class="mb-2 text-center">
          Aujourd'hui, nous sommes une petite équipe dédiée, mais avec une grande mission :
        </p>
        <ul class="list-disc pl-6 space-y-1 text-left">
          <li>Vous accompagner au quotidien dans la gestion de vos projets.</li>
          <li>Évoluer avec vous en intégrant vos retours directement dans nos mises à jour.</li>
          <li>Briser les barrières entre les outils professionnels et l'expérience utilisateur.</li>
        </ul>
        <blockquote class="mt-4 italic text-center text-primary">
          "Chez Nailit, nous ne vendons pas un logiciel - nous construisons un partenaire de croissance."
        </blockquote>
      </div>

      <!-- Pourquoi nous choisir -->
      <div>
        <h3 class="font-bold text-center mb-2 text-lg">Pourquoi nous choisir ?</h3>
        <ul class="list-disc pl-6 space-y-1 text-left">
          <li>100% indépendants : pas de levée de fonds, pas de pression - juste un produit conçu pour durer.</li>
          <li>Réactifs comme aucun autre : un bug ? Une idée ? Nous répondons en moins de 24h.</li>
          <li>Fonctionnalités pensées PME : pas de blabla, que des outils utiles au quotidien.</li>
        </ul>
      </div>

      <!-- Valeurs -->
      <div class="bg-gray-50 rounded-lg p-6 shadow">
        <h3 class="font-bold text-center mb-2 text-lg">Nos valeurs</h3>
        <ul class="list-disc pl-6 space-y-1 text-left">
          <li>Transparence et honnêteté</li>
          <li>Accessibilité pour tous</li>
          <li>Écoute et amélioration continue</li>
          <li>Proximité avec les utilisateurs</li>
        </ul>
      </div>

      <div class="text-center">
        <p>Rejoignez l'aventure - chaque email, chaque avis, chaque suggestion compte pour nous. 🚀</p>
      </div>
    </div>
  </section>

  <FAQSection :faq="faq" />

  <!-- Formulaire de contact -->
  <section class="py-16 px-4 md:px-32 bg-white">
    <div class="max-w-2xl mx-auto">
      <h2 class="text-2xl font-bold mb-8 text-center">Contactez-nous</h2>
      <form class="bg-gray-50 rounded-lg shadow p-8 flex flex-col gap-6 text-left" @submit.prevent="submitForm">
        <div>
          <label class="block text-gray-700 font-semibold mb-2" for="name">Nom</label>
          <input id="name" type="text" class="w-full px-4 py-2 h-11 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary" v-model="form.name" required>
        </div>
        <div>
          <label class="block text-gray-700 font-semibold mb-2" for="email">Email</label>
          <input id="email" type="email" class="w-full px-4 py-2 h-11 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary" v-model="form.email" required>
        </div>
        <div>
          <label class="block text-gray-700 font-semibold mb-2" for="type">Type de demande</label>
          <select id="type" class="w-full px-4 py-2 h-11 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary" v-model="form.type" required>
            <option value="" disabled>Choisissez un type</option>
            <option value="support">Support</option>
            <option value="partenariat">Partenariat</option>
            <option value="autre">Autre</option>
          </select>
        </div>
        <div>
          <label class="block text-gray-700 font-semibold mb-2" for="message">Message</label>
          <textarea id="message" rows="5" class="w-full px-4 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary" v-model="form.message" required></textarea>
        </div>
        <button type="submit"
          class="callToAction"
          :disabled="loading"
        >
          <span v-if="loading" class="animate-spin mr-2 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
          <span v-if="!loading">Envoyer</span>
          <span v-else>Envoi…</span>
        </button>
      </form>
    </div>
  </section>
</template>

<script>
import FAQSection from '@/components/Showcase/FAQSection.vue';
import HeroSection from '@/components/Showcase/HeroSection.vue';

export default {
  name: 'ShowcaseContact',
  components: {
    FAQSection,
    HeroSection
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        type: '',
        message: ''
      },
      faq: [
        {
          question: "Qu'est-ce que Nailit et à qui s'adresse la solution ?",
          answer: "Nailit est un outil de gestion de projet conçu pour les PME et les indépendants. Il facilite la planification, le suivi et la collaboration au sein des équipes."
        },
        {
          question: "Comment contacter le support en cas de problème ?",
          answer: "Juste en dessous de cette FAQ, vous trouverez un formulaire de contact. Remplissez-le et nous vous répondrons dans les plus brefs délais. Autrement vous pouvez directement nous contacter par email à <a class='text-primary hover:underline' href='mailto:contact@nailit-solutions.com'>contact@nailit-solutions.com</a>."
        },
        {
          question: "Est-ce compatible mobile ?",
          answer: "La comptabilité mobile n'est pas encore disponible, mais nous y travaillons activement pour une future mise à jour."
        },
        {
          question: "Comment fonctionne l'essai gratuit ?",
          answer: "Vous bénéficiez de 30 jours d'accès complet à la création de votre compte, sans engagement ni carte bancaire requise."
        },
        {
          question: "Mes données sont-elles sécurisées ?",
          answer: "Oui, toutes vos données sont chiffrées et stockées sur des serveurs sécurisés en France."
        }
      ],
      loading: false
    };
  },
  methods: {
    async submitForm() {
      this.loading = true;
      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.form)
        });
        if (response.ok) {
          alert("Merci pour votre message !");
          this.form = { name: '', email: '', type: '', message: '' };
        } else {
          alert("Erreur lors de l'envoi du message.");
        }
      } catch (e) {
        alert("Erreur lors de l'envoi du message.");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>